(() => {
  const initializedGalleries = new WeakSet();

  const initGallery = (gallery) => {
    if (initializedGalleries.has(gallery) || typeof window.EmblaCarousel === 'undefined') return;

    const track = gallery.querySelector('[data-embla-container]');
    const slideCount = track ? track.children.length : 0;
    if (!track || slideCount === 0) return;

    initializedGalleries.add(gallery);

    gallery.querySelectorAll('img').forEach((image) => {
      image.setAttribute('draggable', 'false');
    });

    // Single image → keep it static, no carousel.
    if (slideCount < 2) return;
    gallery.classList.add('is-embla');

    const embla = window.EmblaCarousel(gallery, {
      loop: false,
      align: 'start',
      containScroll: 'trimSnaps',
      dragFree: false,
      // Snappier arrow/dot transitions (Embla: higher = slower, range 20–60).
      duration: 20,
    });

    // Preload the neighbours of the selected slide so a swipe never lands on a
    // still-loading lazy image (off-viewport slides don't load until visible).
    const slides = embla.slideNodes();
    const preloadAround = () => {
      const selected = embla.selectedScrollSnap();
      [selected, selected + 1, selected - 1].forEach((i) => {
        slides[i]?.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      });
    };
    embla.on('select', preloadAround);
    preloadAround();

    const media = gallery.closest('.new-design-sets__media');

    const prev = media?.querySelector('[data-sets-prev]');
    const next = media?.querySelector('[data-sets-next]');
    prev?.addEventListener('click', (event) => {
      event.preventDefault();
      embla.scrollPrev();
      event.currentTarget.blur();
    });
    next?.addEventListener('click', (event) => {
      event.preventDefault();
      embla.scrollNext();
      event.currentTarget.blur();
    });

    // Hide the arrow at each edge (loop is off — nothing to scroll to).
    const syncArrows = () => {
      prev?.classList.toggle('is-hidden', !embla.canScrollPrev());
      next?.classList.toggle('is-hidden', !embla.canScrollNext());
    };
    embla.on('select', syncArrows);
    embla.on('reInit', syncArrows);
    syncArrows();

    const dotsWrap = media?.querySelector('[data-sets-dots]');
    // iOS-style sliding window (see featured-products for the full rationale):
    // more than WINDOW dots → all render, but the row is translated so the
    // active dot rides a fixed 5-slot window, sized by distance from centre.
    const DOT_STEP = 12; // px pitch — must match the CSS
    const WINDOW = 5;
    let dots = [];
    if (dotsWrap) {
      const buildDots = () => {
        dotsWrap.replaceChildren();
        dots = embla.scrollSnapList().map((_, index) => {
          const dot = document.createElement('span');
          dot.className = 'new-design-sets__dot';
          dot.addEventListener('click', () => embla.scrollTo(index));
          dotsWrap.appendChild(dot);
          return dot;
        });
        dotsWrap.classList.toggle('new-design-sets__dots--windowed', dots.length > WINDOW);
      };

      const syncDots = () => {
        const count = dots.length;
        const selected = embla.selectedScrollSnap();

        if (count <= WINDOW) {
          dots.forEach((dot, i) => {
            dot.classList.toggle('is-selected', i === selected);
            dot.removeAttribute('data-rank');
          });
          dotsWrap.style.removeProperty('--dots-shift');
          return;
        }

        const half = Math.floor(WINDOW / 2); // 2
        const centre = Math.min(Math.max(selected, half), count - 1 - half);
        dotsWrap.style.setProperty('--dots-shift', `${-(centre - half) * DOT_STEP}px`);

        dots.forEach((dot, i) => {
          dot.classList.toggle('is-selected', i === selected);
          const dist = Math.abs(i - centre);
          dot.dataset.rank = dist > half ? 'out' : String(dist);
        });
      };

      buildDots();
      embla.on('select', syncDots);
      embla.on('reInit', () => {
        if (embla.scrollSnapList().length !== dots.length) buildDots();
        syncDots();
      });
      syncDots();
    }

    window.addEventListener('load', () => embla.reInit(), { once: true });
  };

  const initGalleries = (root = document) => {
    if (typeof window.EmblaCarousel === 'undefined') return false;
    root.querySelectorAll('[data-sets-gallery]').forEach(initGallery);
    return true;
  };

  const initWhenReady = (root = document, attemptsLeft = 60) => {
    if (initGalleries(root) || attemptsLeft <= 0) return;
    window.setTimeout(() => initWhenReady(root, attemptsLeft - 1), 50);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWhenReady(), { once: true });
  } else {
    initWhenReady();
  }

  window.addEventListener('load', () => initWhenReady(), { once: true });
  document.addEventListener('shopify:section:load', (event) => initWhenReady(event.target));
})();
